import { makeAutoObservable, runInAction } from "mobx";
import {
    IAddressCategory,
    IReturnMethod,
    IPlatformLocation,
    RETURN_METHOD_TYPE,
    SHIPMENT_NOTIFICATION,
    ShippingInstructionsChangeTrigger,
    SLIP_BARCODE_TYPE,
    CREATE_RETURN_IN_INTEGRATION_TYPE,
} from "../return-method-types";
import {IChangeable, ISaveable, WithOld} from "../../../stores/types";
import ReturnMethodsService, { IDefualtShippingInstructionsArguments, IReturnMethodsSettings } from "../../../services/api/return-methods-service";
import { ShippingLabelSettingsStore } from "./shipping-label-settings-store";
import ReturnMethodIntegrationConnectionStore from "./return-method-integration-connection-store"
import { IOption } from "../../../components/inputs/select-dropdown/select-dropdown";
import ReturnAddressStore from "./return-address-store";
import { IShippingConnection, SHIPPING_CONNECTION_CODE } from "../../../models/IntegrationsTypes";
import { ITranslationScopeContext } from "../../../translation/translation-scope";
import translationStore from "../../../translation/stores/translations-store";
import { trackShopperTextObjectSaved } from "../../../translation/metrics/translations-mixpanel";
import { TYPE, rootContainer } from "../../../services/container/root";
import DropOffSettingsStore from "./drop-off-settings-store";
import { ILocaleItem } from "../../../translation/stores/translations-scope-store";
import AggregatorsInfoStore from "./aggregators-info-store";
import { AUTO_SELECT_OPTION, CREATE_RETURN_IN_WMS_TYPES_MAP, DROP_OFF_LOCATION } from "../constants";
import dashboardSettingsStore from "../../../stores/dashboard-settings-store";
import { PROFILE_CAPABILITIES } from "../../../models/ProfileCapabilities";

export interface IReturnMethodChanges extends Partial<IReturnMethod>{
    _client_id?: number|string;
};

class ReturnMethodStore implements ISaveable, IChangeable<IReturnMethod> {

    private _changes: IReturnMethodChanges = {};
    private _model: IReturnMethod = {} as IReturnMethod;
    private _isSaving: boolean = false;
    private _isDeleting: boolean = false;
    private _isGettingShippingInstructions: boolean = false;
    private _isGettingRates: boolean = false;
    /** holds a map of triggers that changed shipping instructions to show notes */
    private _showInstructionsChangedNote: Partial<Record<ShippingInstructionsChangeTrigger, boolean>> = {};

    /** Sub-stores */
    private _shippingLabelSettingsStore: ShippingLabelSettingsStore;
    private _returnAddressStore: ReturnAddressStore;
    private _dropOffSettingsStore: DropOffSettingsStore;
    private _integrationConnectionStores: ReturnMethodIntegrationConnectionStore[] = [];

    /** Translations properties */
    private _currentTranslationKeys: string[] = [];
    private readonly _translatableFields: string[] = [
        'display_name',
        'description',
        'instruction',
    ];
    private _scope = 'dashboard-return-method-editor';

    /** UI properties */
    private _shouldShowRatesPopup: boolean = false;

    constructor(
        model: IReturnMethod | undefined,
        private _settings: IReturnMethodsSettings,
        private _aggregatorsInfoStore: AggregatorsInfoStore,
        private _removeReturnMethodFromList: (store: ReturnMethodStore) => void,
        private _updateCategoriesLists: (store: ReturnMethodStore) => void,
        private _setTranslation: (key: string, translation: string, locale?: string | undefined, type?: string | undefined) => void = () => {},
        private _localeList: () => ILocaleItem[] = () => [],
        private _refreshShippingIntegrations: () => Promise<void>,
        private _getShippingIntegrations: () => IShippingConnection[],
    ) {
        makeAutoObservable(this);

        this._returnAddressStore = new ReturnAddressStore();
        this._dropOffSettingsStore = new DropOffSettingsStore(
            () => this._getShippingIntegrations(),
            (trigger: ShippingInstructionsChangeTrigger) => this._handleDefaultInstructions(trigger),
        );
        this._shippingLabelSettingsStore = new ShippingLabelSettingsStore(
            this._settings,
            this._returnAddressStore,
            this._aggregatorsInfoStore,
            (trigger: ShippingInstructionsChangeTrigger) => this._handleDefaultInstructions(trigger),
            () => this._getShippingIntegrations(),
        );

        if (model) {
            this._setModel(model);
        }
	}

    private get createReturnInWms() { return this._changes.create_return_in_wms ?? this._model.create_return_in_wms; }
    private get returnMethodsService(): ReturnMethodsService {
		return rootContainer.get<ReturnMethodsService>(TYPE.ReturnMethodsService);
	}

    // return method properties:
    public get id() { return this._changes._client_id ?? this._model.id; }
    public get displayName() { return this._changes.display_name ?? this._model.display_name; }
    public get name() { return this._changes.name ?? this._model.name; }
    public get shipmentNotification() { return this._changes.shipment_notification ?? this._model.shipment_notification; }
    public get description() { return this._changes.description ?? this._model.description; }
    public get instruction() { return this._changes.instruction ?? this._model.instruction; }
    public get methodType() { return this._changes.type ?? this._model.type; }
    public get hasPackingSlip() { return this._changes.has_packing_slip ?? this._model.has_packing_slip; }
    public get hasAddressSlip() { return this._changes.has_address_slip ?? this._model.has_address_slip; }
    public get dropOffLocationId() { return this._changes.shopify_drop_off_location_id ?? this._model.shopify_drop_off_location_id; }
    public get dropOffLocatorUrl() { return this._changes.drop_off_locator_url ?? this._model.drop_off_locator_url; }
    public get shopifyRestockLocationId() { return this._changes.shopify_restock_location_id ?? this._model.shopify_restock_location_id; }
    public get isHidden() { return this._changes.is_hidden ?? this._model.is_hidden; }
    public get shippingLabelSetting() { return this._shippingLabelSettingsStore.deleted ? undefined :this._shippingLabelSettingsStore; }
    public get returnAddress() { return this._returnAddressStore.deleted ? undefined :this._returnAddressStore ; }
    public get useDynamicAddress() { return this._changes.use_dynamic_address ?? this._model.use_dynamic_address; }
    public get dropOffSettings() { return this._dropOffSettingsStore.deleted ? undefined :this._dropOffSettingsStore ; }
    public get shouldShowRatesPopup() { return this._shouldShowRatesPopup; }
    public get integrationConnection() { return this._integrationConnectionStores?.[0]; }
    public get hasInventoryTrackingConnections() { return this._settings.inventory_tracking_connections?.length > 0; }

    /** used for showing the correct state at the restock tuggle button */
    public get restockAtThisAddress() {
        return this.returnAddress?.locationId === this.shopifyRestockLocationId;
    }

    public get aggregatorCode(): SHIPPING_CONNECTION_CODE|undefined {
        if (this.methodType === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL) {
            return this._shippingLabelSettingsStore.aggregatorCode;
        }

        if (this.methodType === RETURN_METHOD_TYPE.DROP_OFF_LOCATION && this.dropOffLocationId === DROP_OFF_LOCATION.DROPOFF_INTEGRATION) {
            return this._dropOffSettingsStore.aggregatorCode;
        }

        return undefined;
    }

    public get aggregatorName(): string|undefined {
        return this._aggregatorsInfoStore.getAggregtorLabel(this.aggregatorCode);
    }

    public isSaving() { return this._isSaving; }

    public isLoading(): boolean {
        return this.isSaving() || this._isDeleting || this._isGettingShippingInstructions || this._isGettingRates;
    }

    public discardChanges() {
        if (!this._model.id) {
            this._removeReturnMethodFromList(this);
        }
        else{
            this._changes = {};
            this._returnAddressStore.discardChanges();
            this._shippingLabelSettingsStore.discardChanges();
            this._dropOffSettingsStore.discardChanges();
            this._updateCategoriesLists(this);
            this.clearInstructionChangeNotes();
        }
    }

    public setChanges(changes: IReturnMethodChanges): void {
        this._changes = changes;
        this._handleDefaultInstructions(ShippingInstructionsChangeTrigger.RETURN_METHODD_SET_CHANGES);
    }

    public getUpdatedModel(): IReturnMethod {
        const hasMethodChanges = !!Object.keys(this._changes).length;

        return { ...this._model, ...this._changes,
            return_label_setting: this._shippingLabelSettingsStore.getUpdatedModel(),
            address: this._returnAddressStore.getUpdatedModel(),
            drop_off_setting: this._dropOffSettingsStore.getUpdatedModel(),
            integrations_connections: this._integrationConnectionStores.map(integrationConnectionStore => integrationConnectionStore?.getUpdatedModel()),
            _has_changes: hasMethodChanges,
        };
    }


    private _shouldRefreshShippingConnections(): boolean {
        return this.dropOffSettings?.shippingConnectionId === 0;
    }

    private async _save(): Promise<void> {
        const updatedModel: WithOld<IReturnMethod> = this.getUpdatedModel();
        let savedReturnMethod: IReturnMethod | undefined = undefined;
        this._isSaving = true;
        const action = updatedModel.id ? "update" : "create";

        try {
            if (updatedModel.id) {
                updatedModel.__old = this._model;
                savedReturnMethod = await this.returnMethodsService.updateReturnMethod(updatedModel.id, updatedModel);
            }
            else {
                savedReturnMethod = await this.returnMethodsService.createReturnMethod(updatedModel);
            }
            /** For NEW RGO connections we use fake connection with id 0.
             * On save, we are creating a real connection on the DB and set the new id to the rm.
             * In order for us to show the connection on connections drop down after saving,
             * we need to refresh the connections list.
             */
            if (this._shouldRefreshShippingConnections()) {
                await this._refreshShippingIntegrations();
            }
        } catch (e) {
            console.error(`ReturnMethodStore.save: ${e}`);
        }

        runInAction(() => {
            if (savedReturnMethod) {
                this._setModel(savedReturnMethod);
                const scopeStore = translationStore.loadScope("return-methods");
                trackShopperTextObjectSaved({
                    action,
                    object_type: "return_method",
                    enabled_locale_count: scopeStore.enabledLocaleCount(),
                    translation_debt_created_count: scopeStore.countMissingTranslations(this.getTranslationKeys()),
                });
            }
            this._isSaving = false;
        });
    }

    private async _getRates(): Promise<void> {
        const updatedModel = this.getUpdatedModel();
        this._isGettingRates = true;
        const shouldRequestRates = updatedModel.type === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL;

        try {
            if (shouldRequestRates) {
                const rates = await this.returnMethodsService.getRates(updatedModel);

                runInAction(() => {
                    if (rates) {
                        const foundRate = !!rates.find(rate => {
                            if (this._shippingLabelSettingsStore.defaultCarrierSelectorStore?.serviceCode === AUTO_SELECT_OPTION) {
                                return true;
                            }
                            return rate.serviceCode === this._shippingLabelSettingsStore.defaultCarrierSelectorStore?.serviceCode;
                        });
                        this._shouldShowRatesPopup = !foundRate;
                    }
                    else {
                        this._shouldShowRatesPopup = true;
                    }
                    this._isGettingRates = false;
                });
            }
            else {
                this._isGettingRates = false;
            }
        } catch (e) {
            console.error(`ReturnMethodStore.getRates: ${e}`);
            runInAction(() => {
                this._isGettingRates = false;
            });
        }
    }

    public getWarningText(): string | undefined {
        return this._aggregatorsInfoStore.getWarningTexts(this.aggregatorCode);
    }

    public async save(): Promise<void>  {
        return this._save();
        /** TODO: There is a bug in get rates, we never receive them, for now after commented it out */
        // this._getRates();
    }

    public async delete(): Promise<number | undefined> {
        this._isDeleting = true;
        try {
            const numOfPolicyRules = await this.returnMethodsService.deleteReturnMethod(this._model.id);
            runInAction(()=> {
                this._isDeleting = false;
                if (numOfPolicyRules === 0) {
                    this._removeReturnMethodFromList(this);
                }
            })
            return numOfPolicyRules;
        }
        catch (e) {
            console.log(`Faild to delete return method due to error: ${e}`);
            runInAction(() => {
                this._isDeleting = false;

            });
        }
    }

    public setDeletedTranslations(setDeletedTranslationKeys: ITranslationScopeContext['setDeletedKeys']) {
        const keyList = this.getTranslationKeys();
        setDeletedTranslationKeys(keyList);
    }

    public getTranslationKeys(): string[] {
        const keyList: string[] = [];

        this._translatableFields.forEach(field => {
            const key = this._changes[field as keyof IReturnMethod] ?? this._model[field as keyof IReturnMethod];
            if (key) {
                keyList.push(key.toString());
            }
        });

        /** we need this._currentTranslationKeys property to use it as dependency in react hooks,
         * so that the hook method will run only when the list was changed */
        const isEqualArrays = (this._currentTranslationKeys.length === keyList.length
            && this._currentTranslationKeys.every(k => !!keyList.find(key => key === k)));
        if (!isEqualArrays) {
            this._currentTranslationKeys = keyList;
        }

        return this._currentTranslationKeys;
    }

    public hasChanges(): boolean {
        return !!Object.keys(this._changes).length ||
            !!this._shippingLabelSettingsStore.hasChanges() ||
            !!this._returnAddressStore.hasChanges() ||
            !!this._dropOffSettingsStore.hasChanges();
    }

    private _setModel(model: IReturnMethod): void {
        const toBool = (v: undefined | any) => Boolean(v);
        this._model = {
            ...model,
            has_packing_slip: toBool(model.has_packing_slip),
            has_address_slip: toBool(model.has_address_slip)
        };
        this._changes = {} as IReturnMethodChanges;
        /** generate all sub stores here to update their model and reset changes */
        this._returnAddressStore.setModel(this._model.address);
        this._dropOffSettingsStore.setModel(this._model.drop_off_setting);
        this._shippingLabelSettingsStore.setModel(this._model.return_label_setting);

        this._integrationConnectionStores = model.integrations_connections?.map(connection => {
            return new ReturnMethodIntegrationConnectionStore(connection);
        });
    }

    public closeRatesPopup() {
        this._shouldShowRatesPopup = false;
    }

    public isAddressConfigurable(): boolean {
        const aggregatorCode = this.aggregatorCode;
        return this._aggregatorsInfoStore?.isAddressConfigurable(aggregatorCode);
    }

    public isIdEquals(id?:string|number): boolean {
        return this._model.id === Number(id) || this._changes._client_id === (id ?? '').toString();
    }

    private _handleDefaultInstructions(trigger: ShippingInstructionsChangeTrigger) {
        const aggregatorCode = this.aggregatorCode;

        if (this.methodType === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL && !aggregatorCode) {
            return;
        }

        if (this.methodType === RETURN_METHOD_TYPE.DROP_OFF_LOCATION && this.dropOffLocationId === DROP_OFF_LOCATION.DROPOFF_INTEGRATION && !aggregatorCode) {
            return;
        }

        this._setDefaultShippingInstructions(trigger);
    }

    private async _setDefaultShippingInstructions(trigger: ShippingInstructionsChangeTrigger): Promise<void> {

        this._isGettingShippingInstructions = true;
        try {
            let labelType = null;
            if (this.methodType === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL) {
                labelType = this._shippingLabelSettingsStore.labelType;
            }

            const address = this._returnAddressStore.isEmpty() ? undefined : this._returnAddressStore.getUpdatedModel();
            const neededArguments: IDefualtShippingInstructionsArguments = {
                type: this.methodType,
                instruction: this.instruction,
                address: address,
                aggregatorCode: this.aggregatorCode,
                labelType: labelType,
                localeList: this._localeList()
            };

            const  defaultShippingInstructions = await this.returnMethodsService.getdefaultShippingInstructions(neededArguments);

            runInAction(() => {
                this._isGettingShippingInstructions = false;
                if (defaultShippingInstructions && defaultShippingInstructions.length) {
                    /** we might receive a list of empty translations - in this case we will not override existing instructions with empty strings */
                    /** if we did not receive EN translation - treating it as empty */
                    const enInstruction = defaultShippingInstructions.find(instr => instr.locale === 'en');
                    if (!enInstruction?.translation || enInstruction.translation === '') {
                        return;
                    }
                    /** overriding instructions with defaluts */
                    defaultShippingInstructions?.forEach(translation => this._setTranslation(translation.key, translation.translation, translation.locale, translation.type));
                    this._showInstructionsChangedNote[trigger] = true;
                }
            })
        } catch {
            this._isGettingShippingInstructions = false;
        }
    }

    public clearInstructionChangeNotes(): void {
        this._showInstructionsChangedNote = {};
    }

    public getInstructionsChangedNote(trigger: ShippingInstructionsChangeTrigger, getTranslation: (key: string, scope: string) => string): string|undefined {
        if (this._showInstructionsChangedNote[trigger]) {
            return getTranslation('INSTRUCTIONS_CHANGE_WARNING_NOTE', this._scope);
        }
        return undefined;
    }

    // Details card functionality:
    public async setMethodType(v: RETURN_METHOD_TYPE): Promise<void>
    {
        this._changes.type = v;
        /** move the return method to the correct category */
        this._updateCategoriesLists(this);
        /** according to method type we should delete or undelete stores */
        this._returnAddressStore.setDeleted(!this.showAddressCard());
        this._shippingLabelSettingsStore.setDeleted(v !== RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL);
        this._dropOffSettingsStore.setDeleted(!this.isDropOffWithIntegration());

        this._handleDefaultInstructions(ShippingInstructionsChangeTrigger.RETURN_METHODD_TYPE);
    }

    public setDropOffLocationId(v: string) {
        this._changes.shopify_drop_off_location_id = v;
        this._dropOffSettingsStore.setDeleted(!this.isDropOffWithIntegration());
        this._returnAddressStore.setDeleted(!this.showAddressCard());
        this._handleDefaultInstructions(ShippingInstructionsChangeTrigger.DROP_OFF_LOCATION);
    }

    public setDropOffLocatorUrl(v: string) {
        this._changes.drop_off_locator_url = v;
    }

    public setHasPackingSlip(v: boolean) {
        this._changes.has_packing_slip = v;
    }

    public setHasAddressSlip(v: boolean) {
        this._changes.has_address_slip = v;
    }

    public showAddressSlipToggle() {
        return this.methodType === RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER;
    }

    public get packingSlipBarcodeType() {
        /** default is ORDER_ID */
        return this._changes.slip_barcode_id_type ?? this._model.slip_barcode_id_type ?? SLIP_BARCODE_TYPE.ORDER_ID;
    }

    public setPackingSlipBarcodeType(v: SLIP_BARCODE_TYPE) {
        this._changes.slip_barcode_id_type = v;
    }

    public get addressSlipBarcodeType() {
        /** default is ORDER_ID */
        return this._changes.address_slip_barcode_id_type ?? this._model.address_slip_barcode_id_type ?? SLIP_BARCODE_TYPE.ORDER_ID;
    }

    public setAddressSlipBarcodeType(v: SLIP_BARCODE_TYPE) {
        this._changes.address_slip_barcode_id_type = v;
    }

    public showPackingSlipToggle() {
        return this.methodType !== RETURN_METHOD_TYPE.NO_SHIPMENT_REQUIRED;
    }

    // Advanced card functionality:
    public showAdvancedCard(): boolean {
        return this.methodType === RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER;
    }

    /**
     * display address location UI if method type is NOT "Ship with any carrier" OR one of slips toggles is turned ON
     */
    public showReturnAddressSection(): boolean {
        switch (this.methodType) {
            case RETURN_METHOD_TYPE.DROP_OFF_LOCATION:
                return !this.isDropOffWithIntegration();
            default:
                return !this.isShipWithAnyCarrierSelected() || this.isAddressOrPackingSlipEnabled();
        }
    }

    public isDropOffWithIntegration(): boolean {
        return this.methodType === RETURN_METHOD_TYPE.DROP_OFF_LOCATION && this.dropOffLocationId === DROP_OFF_LOCATION.DROPOFF_INTEGRATION;
    }

    public isShipWithAnyCarrierSelected(): boolean {
        return this.methodType === RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER;
    }

    public setShipmentNotificationType(v: SHIPMENT_NOTIFICATION): void {
        this._changes.shipment_notification = v;
    }

    // build dropdowns section:
    public getReturnAdressesList(getTranslation: (key: string, scope: string) => string): IAddressCategory[] {
        let list = []
        const listFields =  this.createAddressOptions();
        list.push(
            {
                category: getTranslation('ONLINE_INVENTORY', this._scope),
                options: listFields.onlineOptions,
            },
            {
                category: getTranslation('OTHER_LOCATIONS', this._scope),
                options: listFields.otherOptions,
            },
            {
                category: getTranslation('CUSTOM_ADDRESS', this._scope),
                options: [{
                    value: DROP_OFF_LOCATION.CUSTOM_ADRESS,
                    label: getTranslation('CUSTOM_ADDRESS', this._scope),
                },]
            },
        )

        /** Add use dynamic address for merchants with MCF integration */
        if (this._settings.should_use_dynamic_address && this.methodType === RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL) {
            list[1].options.push({
                value: DROP_OFF_LOCATION.DYNAMIC_ADDRESS,
                label: getTranslation('DYNAMIC_ADDRESS', this._scope),
            })
        }

        return list;
    }

    public getCreateReturnInWmsOptionsList(getTranslation: (key: string, scope: string) => string): IOption[] {
        const options = [CREATE_RETURN_IN_WMS_TYPES_MAP[CREATE_RETURN_IN_INTEGRATION_TYPE.CREATE_RETURN_IN_INTEGRATION_NONE]];

        if (this._settings.inventory_tracking_connections?.length > 1) {
            options.push(CREATE_RETURN_IN_WMS_TYPES_MAP[CREATE_RETURN_IN_INTEGRATION_TYPE.CREATE_RETURN_IN_INTEGRATION_ALL]);
        }

        const translatedOptions = options.map(option => ({
            value: option.value,
            label: getTranslation(option.label, this._scope)
        }));

        return [
            ...translatedOptions,
            ...this.createCreateReturnInWmsOptions(),
        ];
    }

    public getSelectedCreateReturnInWmsOption(getTranslation: (key: string, scope: string) => string): IOption | undefined {
        const createReturnInWmsOptionsList = this.getCreateReturnInWmsOptionsList(getTranslation);
        const value = this.createReturnInWms === CREATE_RETURN_IN_INTEGRATION_TYPE.CREATE_RETURN_IN_INTEGRATION_BY_LIST
                ? this.integrationConnection?.integration_connection_id
                : this.createReturnInWms;
        return createReturnInWmsOptionsList.find(option => option.value === value);
    }

    public getDropOffLocationsList(getTranslation: (key: string, scope: string) => string): IOption[] {
        return [
            {
                value: DROP_OFF_LOCATION.CUSTOM_ADRESS,
                label: getTranslation('PRE_DEFINED_LOCATION', this._scope),
            },
            {
                value: DROP_OFF_LOCATION.DROPOFF_INTEGRATION,
                label: getTranslation('DROP_OFF_LOCATION_VIA_INTEGRATION', this._scope),
            },
        ];
    }

    public getRestockingLocationsList(getTranslation: (key: string, scope: string) => string): IAddressCategory[] {
        let list = []
        const listFields = this.createAddressOptions();
        list.push(
            {
                category: getTranslation('DYNAMIC_LOCATION', this._scope),
                options: [{
                    value: DROP_OFF_LOCATION.FULFILLMENT_LOCATION,
                    label: getTranslation('FULFILLMENT_LOCATION', this._scope),
                }]
            },
            {
                category: getTranslation('ONLINE_INVENTORY', this._scope),
                options: listFields.onlineOptions,
            },
            {
                category: getTranslation('OTHER_LOCATIONS', this._scope),
                options: listFields.otherOptions,
            },
        );
        return list;
    }

    private createAddressOptions(){
        let onlineOptions: IOption[] = [];
        let otherOptions: IOption[] = [];

        // Sort platform locations according to category:
        this._settings.platform_locations_list.forEach(location => {
            if (location.fulfillsOnlineOrders){
                onlineOptions.push({
                    value: location.location_id,
                    label: location.name,
                });
            }
            else {
                otherOptions.push({
                    value: location.location_id,
                    label: location.name,
                });
            }
        });

        return {onlineOptions, otherOptions};
    }

    private createCreateReturnInWmsOptions(): IOption[] {
        return this._settings.inventory_tracking_connections?.map(connection => ({
                value: connection.id,
                label: connection.internal_name,
        }));
    }

    public setCreateReturnInWms(v: CREATE_RETURN_IN_INTEGRATION_TYPE | number) {
        switch (v as CREATE_RETURN_IN_INTEGRATION_TYPE) {
            case CREATE_RETURN_IN_INTEGRATION_TYPE.CREATE_RETURN_IN_INTEGRATION_ALL:
            case CREATE_RETURN_IN_INTEGRATION_TYPE.CREATE_RETURN_IN_INTEGRATION_NONE:
                this._changes.create_return_in_wms = v as CREATE_RETURN_IN_INTEGRATION_TYPE;
                this._integrationConnectionStores.forEach(integrationConnectionStore => integrationConnectionStore.setDeleted(true));
                break;
            default:
                this._changes.create_return_in_wms = CREATE_RETURN_IN_INTEGRATION_TYPE.CREATE_RETURN_IN_INTEGRATION_BY_LIST;
                const connectionModel = {
                    return_method_id: this._model.id,
                    integration_connection_id: v as number,
                };

                if (this._integrationConnectionStores[0]) {
                    //
                    // We call setDeleted(falsed) because of following scenario:
                    //   - connection is selected in DB
                    //   - user selects "Do not update" or "Update all", at this point connection has _deleted
                    //   - user selects connection, at this point connection still has _deleted
                    //  So we need to remove _delete from the store ^^^
                    //
                    this._integrationConnectionStores[0].setDeleted(false);
                }
                else {
                    // if this is the first time user selects integration connection, we need to insert a new
                    // store instance
                    this._integrationConnectionStores = [new ReturnMethodIntegrationConnectionStore()];
                }
                this._integrationConnectionStores[0].setChanges(connectionModel);

                break;
        }
    }

    private setUseDynamicAddress(v: boolean) {
        this._changes.use_dynamic_address = v;
    }

    //return address card functionality:
    public showAddressCard() {
        switch (this.methodType) {
            case RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER:
            case RETURN_METHOD_TYPE.DROP_OFF_LOCATION:
                return true;
            case RETURN_METHOD_TYPE.SHIP_WITH_PRE_PAID_LABEL: {
                const capability = dashboardSettingsStore.resolveCapability(
                    PROFILE_CAPABILITIES.RETURN_METHOD_SHIP_WITH_PREPAID_LABEL
                );
                return capability.isReturnAddressSupported;
            }
            default:
                return false;
        }
    }

    public isAddressOrPackingSlipEnabled() {
        return this.hasAddressSlip || this.hasPackingSlip;
    }

    public setSelectedReturnAddress(option: IOption) {
        this.setUseDynamicAddress(false);

        switch (option.value) {
            case DROP_OFF_LOCATION.CUSTOM_ADRESS:
                /** For custom addresses we need to reset the previos changes and use the custum address fields to set the new changes */
                this._returnAddressStore.discardChanges();
                this._returnAddressStore.setLocationId(null);
                return;
            case DROP_OFF_LOCATION.DYNAMIC_ADDRESS:
                this.setHasAddressSlip(true);
                this.setUseDynamicAddress(true);
                /** Delete existing return address */
                this._returnAddressStore.setDeleted(true);
                return;
            default:
                /** when selecting shopify address from selector  */
                let selectedReturnAddress = this.getLocation(option.value as string);
                this._returnAddressStore.setSelectedAddress(selectedReturnAddress);
                this._returnAddressStore.setDeleted(false);
                return;
        };
    }


    /** find platform location according to selected location id */
    public getLocation(id: string): IPlatformLocation | undefined {
        return this._settings.platform_locations_list.find(location =>
            location.location_id === id
        );
    }

    public getAddressReasonNote(): string {
        const aggregatorCode = this._shippingLabelSettingsStore.aggregatorCode;
        return this._aggregatorsInfoStore?.getAddressReasonNote(aggregatorCode) ?? '';
    }

    public getCountriesOptionsList(): IOption[] {
        return this._settings.countries_list?.map((country) => {
            return {
                label: country.name,
                value: country.code,
            }
        }) ?? [];
    }

    private isCustomAddress() {
        let locationId = this.returnAddress?.locationId;
        if (!locationId) {
            return !this.useDynamicAddress;
        }
        return false;
    }

    public showCustomAddressFields() {
        if (this.isCustomAddress()) {
            if (this.methodType === RETURN_METHOD_TYPE.DROP_OFF_LOCATION) {
                return this.hasPackingSlip;
            }
            return true;
        }
        return false;
    }

    public showCustomAddressNote() {
        if (this.isCustomAddress()) {
            if (this.methodType === RETURN_METHOD_TYPE.DROP_OFF_LOCATION) {
                return !this.hasPackingSlip;
            }
        }
        if (this.methodType === RETURN_METHOD_TYPE.SHIP_WITH_ANY_CARRIER) {
            return true;
        }
        return false;
    }

    //restock functionality:
    /** used to show / hide restock tuggle button */
    public showRestockAtThisAddress() {
        const fulfillsOnlineOrders = !!this.getLocation(this._returnAddressStore.locationId as string)?.fulfillsOnlineOrders;
        return this.showReturnAddressSection() && fulfillsOnlineOrders;
    }

    public setShopifyRestockLocationId(value: string) {
        this._changes.shopify_restock_location_id = value;
    }

    public setRestockAtThisAddress(v: boolean) {
        if (v) {
            this.setShopifyRestockLocationId(this.returnAddress?.locationId as string)
        } else {
            this.setShopifyRestockLocationId(DROP_OFF_LOCATION.FULFILLMENT_LOCATION);
        }
    }

    public isLocatorUrlConfigurable(): boolean {
        if (this.methodType !== RETURN_METHOD_TYPE.DROP_OFF_LOCATION || this.dropOffLocationId !== DROP_OFF_LOCATION.DROPOFF_INTEGRATION) {
            return true;
        }
        return this._aggregatorsInfoStore.isLocatorUrlConfigurable(this.aggregatorCode);
    }

    public getDropOffIntegrationAggregators(): IOption[] {
        let dropOffIntegrationAggregators = {} as IOption[];
        const shippingIntegrations = this._getShippingIntegrations();
        const filteredIntegrations = shippingIntegrations.filter( integration =>
            this._aggregatorsInfoStore.isDropOffAggregator(integration.aggregator_code) ?? false);

        if (filteredIntegrations) {
            dropOffIntegrationAggregators = filteredIntegrations.map (
                (integration) => { return {
                    label: integration.internal_name,
                    value: integration.id
                }
            });
        }

        return dropOffIntegrationAggregators;
    }

    public areInstructionsEditable(): boolean {
        if (this._isGettingShippingInstructions) {
            return false;
        }

        return this._aggregatorsInfoStore?.areInstructionsEditable(this.aggregatorCode) ?? true;
    }

}

export default ReturnMethodStore;
